import { z } from 'zod';
import { AccessState, Id } from '../primitives.js';

/**
 * Подписка клиники: то, что видит и делает владелец на экране оплаты.
 *
 * Схемы стоят здесь, а не в сервисе, по той же причине, по которой здесь стоят
 * все остальные: биллинг — отдельный процесс со своей базой, и единственное,
 * что связывает его с клиентом, это форма ответа. Разъехавшись, она сломает не
 * список пациентов, а страницу оплаты — то есть выручку.
 *
 * Состояние подписки намеренно совпадает с `AccessState`: ядро получает из
 * биллинга ровно его и по нему решает, разрешена ли запись. Два разных
 * перечисления однажды разошлись бы, и клиника с «активной» подпиской не смогла
 * бы записать пациента.
 *
 * Модуль: docs/03-modules/13-billing-subscriptions.md
 */

export const SubscriptionState = AccessState;
export type SubscriptionState = z.infer<typeof SubscriptionState>;

/** Что доступно в текущем состоянии. Правила считает сервер, экран их не изобретает. */
export const SubscriptionCapabilities = z.object({
  read: z.boolean(),
  write: z.boolean(),
  export: z.boolean(),
  pull: z.boolean(),
  push: z.boolean(),
});
export type SubscriptionCapabilities = z.infer<typeof SubscriptionCapabilities>;

/**
 * О чём предупредить заранее.
 *
 * Приходит с сервера уже решённым, а не вычисляется на экране: «за сколько дней
 * беспокоить» — правило коммерческого контура, и оно обязано быть одинаковым в
 * письме, в интерфейсе и в отчёте.
 */
export const SubscriptionWarning = z.object({
  kind: z.enum(['trial_ending', 'charge_soon', 'card_expiring']),
  days: z.number().int(),
});
export type SubscriptionWarning = z.infer<typeof SubscriptionWarning>;

export const PaymentMethodView = z.object({
  title: z.string().nullable(),
  last4: z.string().nullable(),
  cardType: z.string().nullable(),
  /** `YYYY-MM`: у карты нет дня окончания, только месяц. */
  expiresOn: z.string().nullable(),
  expiringSoon: z.boolean(),
});
export type PaymentMethodView = z.infer<typeof PaymentMethodView>;

export const SubscriptionView = z.object({
  state: SubscriptionState,
  stateLabel: z.string(),
  plan: z.object({
    code: z.string(),
    name: z.string(),
    /** Копейки: в продукте нет ни одной суммы с плавающей точкой. */
    priceMinor: z.number().int(),
    currency: z.string(),
  }),
  capabilities: SubscriptionCapabilities,
  trialEndsOn: z.iso.date().nullable(),
  currentPeriodStart: z.iso.date().nullable(),
  /** Он же день следующего списания. */
  currentPeriodEnd: z.iso.date().nullable(),
  daysLeft: z.number().int().nullable(),
  cancelled: z.boolean(),
  cancelReason: z.string().nullable(),
  lastFailure: z.string().nullable(),
  lastFailureLabel: z.string().nullable(),
  /** До какого дня можно заплатить, прежде чем запись закроется. */
  payBefore: z.iso.date().nullable(),
  method: PaymentMethodView.nullable(),
  warning: SubscriptionWarning.nullable(),
});
export type SubscriptionView = z.infer<typeof SubscriptionView>;

/**
 * Ссылка на оплату.
 *
 * Возврат по ней подтверждением не является: платёж подтверждает только
 * проверенный вебхук. Экран, показавший «оплачено» по факту возврата из
 * браузера, однажды покажет это при неудачном платеже.
 */
export const CheckoutResult = z.object({
  confirmationUrl: z.url(),
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  amountMinor: z.number().int(),
});
export type CheckoutResult = z.infer<typeof CheckoutResult>;

export const InvoiceView = z.object({
  id: Id,
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  amountMinor: z.number().int(),
  status: z.enum(['pending', 'succeeded', 'canceled', 'failed']),
  failureLabel: z.string().nullable(),
  paidAt: z.iso.datetime().nullable(),
  receiptId: Id.nullable(),
  /** Оплачено, а чека ещё нет: оператор фискальных данных не ответил. */
  receiptPending: z.boolean(),
});
export type InvoiceView = z.infer<typeof InvoiceView>;

export const InvoiceListResponse = z.object({ invoices: z.array(InvoiceView) });
export type InvoiceListResponse = z.infer<typeof InvoiceListResponse>;

/**
 * Фискальный чек в том виде, в котором его зарегистрировал оператор. Сочинять
 * недостающие реквизиты нельзя: документ должен совпадать с тем, что видит
 * налоговая, иначе он бесполезен обеим сторонам.
 */
export const ReceiptView = z.object({
  id: Id,
  kind: z.enum(['payment', 'refund']),
  fiscalDocumentNumber: z.string().nullable(),
  fiscalStorageNumber: z.string().nullable(),
  fiscalAttribute: z.string().nullable(),
  registeredAt: z.iso.datetime().nullable(),
  payload: z.unknown(),
});
export type ReceiptView = z.infer<typeof ReceiptView>;

export const CancelSubscriptionRequest = z.object({
  reason: z.string().max(500).default(''),
});
export type CancelSubscriptionRequest = z.infer<typeof CancelSubscriptionRequest>;
